import { Component, OnInit, Inject, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MeteringType } from '@enums';
import { MeteringPoint, MeteringPointAddress, MeteringPointGroup } from '@model';
import { distinctUntilChanged, map, filter, take, debounceTime, switchMap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromMeteringStore from '@appStore/metering-points';
import * as fromCustomerStore from '@appStore/customers';
import { containsCaseInsensitive } from '@utils/sx-utilities';
import { SubSink } from 'subsink';

@Component({
    selector: 'app-metering-group-dialog',
    templateUrl: './metering-group-dialog.component.html',
    standalone: false
})
export class MeteringGroupDialogComponent implements OnInit, OnDestroy {
  meteringType: MeteringType;
  newGroupName: string;
  addingNewGroup: boolean;
  public keyUp = new Subject<any>();
  meteringGroups: MeteringPointGroup[];
  private subs = new SubSink();

  // New
  searchAdresses: boolean;
  meteringPoints: MeteringPoint[];
  meteringPointsView: MeteringPoint[] = [];
  selectedMeteringPoints: MeteringPoint[] = [];
  searchFilter: string;
  public keyUpMP = new Subject<any>();

  constructor(
    public dialogRef: MatDialogRef<MeteringGroupDialogComponent>,
    private meteringStore: Store<fromMeteringStore.State>,
    private customerStore: Store<fromCustomerStore.State>,
    private changeDetectorRef: ChangeDetectorRef,
    private store: Store<fromMeteringStore.State>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.subs.sink = this.keyUpMP
    .pipe(
      map(event => event.target.value),
      debounceTime(300),
      distinctUntilChanged()
    )
    .subscribe(selectedFilter => {
      this.updateView(selectedFilter);
    });

    this.keyUp
      .pipe(
        map(event => event.target.value),
        debounceTime(300),
        distinctUntilChanged()
    )
    .subscribe(value => {
      this.isValueSet(value);
    });

    this.meteringType = this.data.meteringType;
  }

  get groupNameControlInvalid() {
    if (this.addingNewGroup) {
      let exist = this.meteringGroups.some(o => o.name.toLowerCase().trim() === this.newGroupName.trim().toLowerCase());
      return exist;
    }
    return false;
  }

  ngOnInit() {
    // Fetch meteringPointGroups from Store for MeteringType
    this.subs.sink = this.store
      .select(fromMeteringStore.getMeteringPointGroups)
      .pipe(
        filter(x => !!x),
        take(1)
      )
      .subscribe(mpgs => {
        this.meteringGroups = mpgs
          .filter(mpg => mpg.meteringType === this.meteringType)
          .map((mpg: MeteringPointGroup) => ({ ...mpg, selected: false }));
      });

      this.initNormalMpSubscription();
  }

  initNormalMpSubscription = () => {
    this.subs.sink = this.meteringStore.select(fromMeteringStore.getMeteringPointsWithAdresses).subscribe(mps => {
      // Load meteringPoint if customer is not white label
      this.subs.sink = this.customerStore.select(fromCustomerStore.getCustomers).subscribe(customers => {

        // Filter out mpIds where customer is white label
        if (customers) {
          mps = mps.filter(mp => {
            let customer = customers.find(c => c.id === mp.customerId);
            return customer ? !customer.isWhiteLabel : false;
          });


          this.meteringPoints = mps
            .filter(mp => mp.meteringType === this.meteringType)
            .map(mp => ({
            ...mp,
            selected: false
          }));
        
          this.meteringPointsView = this.meteringPoints;
        }

      });
    });
  }

  updateView(filterString: string) {
    if (this.searchAdresses) {
      if (filterString === '') {
        this.meteringPointsView = [...this.meteringPoints];
      } else {
        this.meteringPointsView = [
          ...this.meteringPoints.filter(mp => this.foundInAdresses(mp.addresses, filterString) ||
          containsCaseInsensitive(mp.id.toString(), filterString) || mp.selected)
        ];
      }
    } else {
      if (filterString === '') {
        this.meteringPointsView = [...this.meteringPoints];
      } else {
        this.meteringPointsView = [...this.meteringPoints.filter(mp => containsCaseInsensitive(mp.name, filterString) ||
          containsCaseInsensitive(mp.id.toString(), filterString) || mp.selected)];
      }
    }
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  setFilterType(adressFilter: boolean) {
    this.searchAdresses = adressFilter;
  }

  isAllMpsSelected(): boolean {
    return this.meteringPointsView.filter(mp => mp.selected).length === this.meteringPointsView.length;
  }

  foundInAdresses = (addresses: MeteringPointAddress[], filterString: string): boolean => {
    const matched = addresses.filter(address =>
      containsCaseInsensitive(address.street + ' ' + address.streetNo, filterString)
    );
    return matched.length > 0;
  };

  toggleAllMps() {
    let selectAll = !this.isAllMpsSelected();

    for (let mp of this.meteringPointsView) {
      mp.selected = selectAll;
    }
    this.selectedMeteringPoints = this.meteringPointsView.filter(mp => mp.selected);
  }

  selectMeteringPoint(meteringPoint: MeteringPoint) {
    meteringPoint.selected = !meteringPoint.selected;
    this.setSelectedMeteringPoint(meteringPoint.id, meteringPoint.selected, this.meteringPointsView);

    this.selectedMeteringPoints = this.meteringPointsView.filter(mp => mp.selected);
  }

  setSelectedMeteringPoint(id: number, isSelected: boolean, meteringPoints: MeteringPoint[]) {
    for (let mp of meteringPoints) {
      if (mp.id === id) {
        mp.selected = isSelected;
      }
    }
  }

  onSave() {
    if (!this.groupNameControlInvalid) {
      const newGroup = {
        id: 0,
        name: this.newGroupName,
        meteringPoints: [],
        selected: true,
        meteringType: this.meteringType
      } as MeteringPointGroup;

      // Valdidate unique groupname
      this.store.dispatch(new fromMeteringStore.AddMeteringPointGroup(newGroup));


      this.subs.sink = this.store
         .select(fromMeteringStore.getAddMeteringPointGroupSuccess)
         .pipe(
           filter(x => !!x),
           take(1),
           switchMap(x => this.store.select(fromMeteringStore.getAddedMeteringPointGroup))
         )
         .subscribe(addedMpg => {
            if (addedMpg) {
              if (this.selectedMeteringPoints && this.selectedMeteringPoints.length > 0) {
                addedMpg = {...addedMpg, meteringPoints: this.selectedMeteringPoints, selected: true};
                this.store.dispatch(new fromMeteringStore.UpdateMeteringPointGroup(addedMpg));
              }

              this.dialogRef.close(addedMpg);
            }
          });
    }
  }

  isValueSet(val: string) {
    if (val === undefined || val === null || val === '') {
      this.addingNewGroup = false;
    } else {
      this.addingNewGroup = true;
    }
  }

 
}
